import { Box, Typography } from '@mui/material';
import { CARD_TEXT, Chip, ProfileCard } from './ProfileCard';
import { domainExtremes } from '../profileText';
import type { ProfileAdministration, StudentProfile } from '../studentProfileApi';
import { ROSTER } from '../tokens';

export type ExitBarKey = 'overall' | 'listening' | 'speaking' | 'reading' | 'writing';
export type ExitBar = { key: ExitBarKey | string; label: string; minimum: number };
export type ExitBars = { state: string; outcome: 'exits' | 'eligible_for_review'; note?: string; bars: ExitBar[]; noExit?: boolean; review?: { bars: ExitBar[]; note?: string } };
export type ExitCriteriaState = ExitBars | null | 'loading' | 'failed';
export type AccessUploaded = boolean | null | 'loading';

const MEASURABLE: readonly string[] = ['overall', 'listening', 'speaking', 'reading', 'writing'];
const OVERALL: ExitBar[] = [{ key: 'overall', label: 'Overall', minimum: 0 }];

function levelOf(admin: ProfileAdministration, key: string): number | null {
  if (key === 'overall') return admin.composite;
  return admin.domains?.[key] ?? null;
}

type ScoreLine = { text: string; belowExit: boolean };

function scoreLine(admin: ProfileAdministration, bar: ExitBar, marks: boolean): ScoreLine {
  if (!MEASURABLE.includes(bar.key)) return { text: `${bar.label} not available`, belowExit: false };
  const level = levelOf(admin, bar.key);
  if (level == null) return { text: `${bar.label} not scored`, belowExit: false };
  return { text: `${bar.label} ${level.toFixed(1)}`, belowExit: marks && level < bar.minimum };
}

function exitMet(admin: ProfileAdministration, bars: ExitBar[]): boolean {
  return bars.length > 0 && bars.every((bar) => {
    const level = MEASURABLE.includes(bar.key) ? levelOf(admin, bar.key) : null;
    return level != null && level >= bar.minimum;
  });
}

function Column({ testId, header, lines }: { testId: string; header: string; lines: (string | ScoreLine)[] }) {
  return (
    <Box data-testid={testId} sx={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: '1 1 0', minWidth: 0 }}>
      <Typography sx={{ ...CARD_TEXT.lead, fontWeight: 600 }}>{header}</Typography>
      {lines.map((line, i) => {
        const { text, belowExit } = typeof line === 'string' ? { text: line, belowExit: false } : line;
        return (
          <Typography key={`${i}-${text}`} component="div" sx={{ ...CARD_TEXT.lead, color: ROSTER.muted, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {text}
            {belowExit && <Chip tone="warn">Below exit</Chip>}
          </Typography>
        );
      })}
    </Box>
  );
}

function Comparison({ criteriaHeader, name, admin, bars, marks }: { criteriaHeader: string | null; name: string; admin: ProfileAdministration; bars: ExitBar[]; marks: boolean }) {
  return (
    <Box sx={{ display: 'flex', gap: '8px' }}>
      {criteriaHeader && <Column testId="exit-criteria" header={criteriaHeader} lines={bars.map((bar) => `${bar.label} ${bar.minimum.toFixed(1)}`)} />}
      <Column testId="exit-student" header={`${name}:`} lines={bars.map((bar) => scoreLine(admin, bar, marks))} />
    </Box>
  );
}

function Note({ children }: { children: string }) {
  return <Typography sx={CARD_TEXT.small}>{children}</Typography>;
}

function alternateLines(admin: ProfileAdministration): string[] {
  const lines = [`Overall ${admin.composite == null ? 'not scored' : `Level ${Math.floor(admin.composite)}`}`];
  const extremes = domainExtremes(admin.domains ?? {});
  if (extremes) lines.push(`${extremes.highest} Level ${Math.floor(admin.domains?.[extremes.highest.toLowerCase()] ?? 0)}`);
  return lines;
}

function emptyReason(profile: StudentProfile, accessUploaded: AccessUploaded): string | null {
  if (profile.access.noScores || accessUploaded === 'loading') return null;
  if (accessUploaded === false) return 'Scores appear here once the district uploads its ACCESS files in Data & Integrations.';
  return 'This student isn’t in the district’s ACCESS file.';
}

function noComparisonReason(criteria: ExitCriteriaState): string | null {
  if (criteria === 'loading') return null;
  if (criteria === 'failed') return 'Exit criteria could not be loaded. Refresh the page to try again.';
  if (criteria?.noExit) return `Students in this grade can’t exit on ACCESS in ${criteria.state}.`;
  return 'Exit criteria aren’t set in Settings.';
}

function Body({ profile, criteria, admin }: { profile: StudentProfile; criteria: ExitCriteriaState; admin: ProfileAdministration }) {
  const name = profile.student.firstName?.trim() || `Student ${profile.student.id}`;
  const marks = !profile.cohorts.includes('newcomer');
  if (profile.access.scale === 'alternate_access') {
    return (
      <>
        <Column testId="exit-student" header={`${name}:`} lines={alternateLines(admin)} />
        <Note>Exit criteria for Alternate ACCESS aren’t set in Settings.</Note>
      </>
    );
  }
  if (criteria === 'loading' || criteria === 'failed' || criteria == null || criteria.noExit || criteria.bars.length === 0) {
    const reason = noComparisonReason(criteria);
    return (
      <>
        <Comparison criteriaHeader={null} name={name} admin={admin} bars={OVERALL} marks={false} />
        {reason && <Note>{reason}</Note>}
      </>
    );
  }
  const review = criteria.review && !exitMet(admin, criteria.bars) ? criteria.review : null;
  return (
    <>
      <Comparison criteriaHeader={criteria.outcome === 'exits' ? 'Criteria:' : 'Review criteria:'} name={name} admin={admin} bars={criteria.bars} marks={marks && criteria.outcome === 'exits'} />
      {criteria.note && <Note>{criteria.note}</Note>}
      {review && <Comparison criteriaHeader="Review criteria:" name={name} admin={admin} bars={review.bars} marks={false} />}
      {review?.note && <Note>{review.note}</Note>}
    </>
  );
}

export function AccessCard({ profile, criteria, accessUploaded }: { profile: StudentProfile; criteria: ExitCriteriaState; accessUploaded: AccessUploaded }) {
  const alternate = profile.access.scale === 'alternate_access';
  const newest = profile.access.administrations[0];
  const reason = newest ? null : emptyReason(profile, accessUploaded);
  return (
    <ProfileCard title={alternate ? 'Alternate ACCESS and Exit' : 'ACCESS and Exit'}>
      {newest ? (
        <Body profile={profile} criteria={criteria} admin={newest} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', p: '12px 16px', borderRadius: '8px', bgcolor: ROSTER.surface }}>
          <Typography sx={CARD_TEXT.strong}>No ACCESS scores on file</Typography>
          {reason && <Typography sx={CARD_TEXT.small}>{reason}</Typography>}
        </Box>
      )}
    </ProfileCard>
  );
}
